import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { Product, CartItem } from '../types'
import { calculateCartTotals } from '../lib/utils'

interface CartStore {
  items: CartItem[]
  taxRate: number
  shippingCost: number
  
  // Actions
  addItem: (product: Product, quantity?: number) => void
  removeItem: (productId: string) => void
  updateQuantity: (productId: string, quantity: number) => void
  clearCart: () => void
  setTaxRate: (rate: number) => void
  setShippingCost: (cost: number) => void
  
  // Computed
  getItemCount: () => number
  getCartTotals: () => {
    subtotal: number
    taxAmount: number
    shippingCost: number
    total: number
  }
}

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      items: [],
      taxRate: 0,
      shippingCost: 0,

      addItem: (product, quantity = 1) => {
        set((state) => {
          const existingItem = state.items.find(
            (item) => item.product.id === product.id
          )

          if (existingItem) {
            // Update quantity if item exists
            return {
              items: state.items.map((item) =>
                item.product.id === product.id
                  ? { ...item, quantity: item.quantity + quantity }
                  : item
              ),
            }
          }

          // Add new item
          return {
            items: [...state.items, { product, quantity }],
          }
        })
      },

      removeItem: (productId) => {
        set((state) => ({
          items: state.items.filter((item) => item.product.id !== productId),
        }))
      },

      updateQuantity: (productId, quantity) => {
        if (quantity <= 0) {
          get().removeItem(productId)
          return
        }

        set((state) => ({
          items: state.items.map((item) =>
            item.product.id === productId ? { ...item, quantity } : item
          ),
        }))
      },

      clearCart: () => {
        set({ items: [] })
      },

      setTaxRate: (rate) => {
        set({ taxRate: rate })
      },

      setShippingCost: (cost) => {
        set({ shippingCost: cost })
      },

      getItemCount: () => {
        return get().items.reduce((sum, item) => sum + item.quantity, 0)
      },

      getCartTotals: () => {
        const state = get()
        return calculateCartTotals(
          state.items.map((item) => ({
            price: item.product.price,
            quantity: item.quantity,
          })),
          state.taxRate,
          state.shippingCost
        )
      },
    }),
    {
      name: 'amplifier-cart',
      partialize: (state) => ({
        items: state.items,
      }),
    }
  )
)
